import { createAsyncThunk, createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { extractRbacAxiosError } from "./rbacApiError";
import { RBACContext, RBACService } from "./RBACService";
import { mergeRoleResourcesById, toPermission, toResource, toVmsRole } from "./normalize";
import type {
  AdminPortalPermissionMatrix,
  AllotablePermissionsView,
  GroupedPermissionsView,
  RbacState,
  RbacToastMessage,
} from "./types";

const initialState: RbacState = {
  vmsRoles: [],
  permissions: [],
  resources: [],
  roleResources: [],
  roleResourcePermissions: [],
  users: [],
  permissionViews: { roleResourceId: null, grouped: null, adminPortal: null },
  allotablePermissions: null,
  loading: false,
  viewsLoading: false,
  pendingActionId: null,
  error: null,
  toasts: [],
};

const mapErr = (error: unknown, fallback: string) => extractRbacAxiosError(error, fallback);

/** Build a stable identifier for in-flight actions so tabs can disable a specific row. */
const buildActionId = (op: string, id?: number | string): string =>
  id === undefined ? op : `${op}:${id}`;

export const initializeRbac = createAsyncThunk("rbac/initialize", async (_, { rejectWithValue }) => {
  try {
    const roleResourceId = RBACContext.getUserRoleResourceId();
    const [vmsRoleRows, permRows, resRows, roleResources, roleResourcePermissions, userBundle] =
      await Promise.all([
        RBACService.listVmsRoles(),
        RBACService.listPermissions(),
        RBACService.listResources(),
        RBACService.listRoleResources(),
        RBACService.listRoleResourcePermissions(),
        RBACService.listUsersUnderRoleResource(roleResourceId),
      ]);

    const mergedRoleResources = mergeRoleResourcesById(
      roleResources,
      userBundle.rolesFromSubTree ?? []
    );

    return {
      vmsRoles: vmsRoleRows.map((r) => toVmsRole(r)),
      permissions: permRows.map((r) => toPermission(r)),
      resources: resRows.map((r) => toResource(r)),
      roleResources: mergedRoleResources,
      roleResourcePermissions,
      users: userBundle.users,
    };
  } catch (error) {
    return rejectWithValue(mapErr(error, "Failed to initialize RBAC data"));
  }
});

/**
 * Common helper used by mutating thunks. Wraps the operation with a pending-id
 * tag, refreshes the dataset on success, and returns a toast-friendly message.
 */
const runMutation = async <T extends unknown>(
  exec: () => Promise<T>,
  refresh: () => Promise<unknown>,
  fallbackMessage: string,
  successMessage: string,
  rejectWithValue: (value: { message: string }) => unknown
) => {
  try {
    await exec();
    await refresh();
    return { successMessage };
  } catch (error) {
    return rejectWithValue({ message: mapErr(error, fallbackMessage) }) as unknown as {
      successMessage: string;
    };
  }
};

export const createVmsRole = createAsyncThunk(
  "rbac/createVmsRole",
  async (payload: { role: string; description?: string }, { dispatch, rejectWithValue }) =>
    runMutation(
      () => RBACService.createVmsRole(payload),
      () => dispatch(initializeRbac()).unwrap(),
      "Failed to create role",
      `Role "${payload.role}" created`,
      rejectWithValue
    )
);

export const updateVmsRole = createAsyncThunk(
  "rbac/updateVmsRole",
  async (
    payload: { roleId: number; role?: string; name?: string; description?: string },
    { dispatch, rejectWithValue }
  ) =>
    runMutation(
      () =>
        RBACService.updateVmsRole(payload.roleId, {
          role: payload.role,
          name: payload.name,
          description: payload.description,
        }),
      () => dispatch(initializeRbac()).unwrap(),
      "Failed to update role",
      "Role updated",
      rejectWithValue
    )
);

export const deleteVmsRole = createAsyncThunk(
  "rbac/deleteVmsRole",
  async (roleId: number, { dispatch, rejectWithValue }) =>
    runMutation(
      () => RBACService.deleteVmsRole(roleId),
      () => dispatch(initializeRbac()).unwrap(),
      "Failed to delete role",
      "Role deleted",
      rejectWithValue
    )
);

export const createPermission = createAsyncThunk(
  "rbac/createPermission",
  async (payload: { action: string; scope: string }, { dispatch, rejectWithValue }) =>
    runMutation(
      () => RBACService.createPermission(payload),
      () => dispatch(initializeRbac()).unwrap(),
      "Failed to create permission",
      `Permission ${payload.action}:${payload.scope} created`,
      rejectWithValue
    )
);

export const updatePermission = createAsyncThunk(
  "rbac/updatePermission",
  async (
    payload: { permissionId: number; action: string; scope: string },
    { dispatch, rejectWithValue }
  ) =>
    runMutation(
      () =>
        RBACService.updatePermission(payload.permissionId, {
          action: payload.action,
          scope: payload.scope,
        }),
      () => dispatch(initializeRbac()).unwrap(),
      "Failed to update permission",
      "Permission updated",
      rejectWithValue
    )
);

export const deletePermission = createAsyncThunk(
  "rbac/deletePermission",
  async (permissionId: number, { dispatch, rejectWithValue }) =>
    runMutation(
      () => RBACService.deletePermission(permissionId),
      () => dispatch(initializeRbac()).unwrap(),
      "Failed to delete permission",
      "Permission deleted",
      rejectWithValue
    )
);

export const createResource = createAsyncThunk(
  "rbac/createResource",
  async (payload: { resourceName: string; parentRole: number }, { dispatch, rejectWithValue }) =>
    runMutation(
      () => RBACService.createResource(payload),
      () => dispatch(initializeRbac()).unwrap(),
      "Failed to create resource",
      `Resource "${payload.resourceName}" created`,
      rejectWithValue
    )
);

export const updateResource = createAsyncThunk(
  "rbac/updateResource",
  async (
    payload: { resourceId: number; resourceName?: string; parentRole?: number },
    { dispatch, rejectWithValue }
  ) =>
    runMutation(
      () =>
        RBACService.updateResource(payload.resourceId, {
          resourceName: payload.resourceName,
          parentRole: payload.parentRole,
        }),
      () => dispatch(initializeRbac()).unwrap(),
      "Failed to update resource",
      "Resource updated",
      rejectWithValue
    )
);

export const deleteResource = createAsyncThunk(
  "rbac/deleteResource",
  async (resourceId: number, { dispatch, rejectWithValue }) =>
    runMutation(
      () => RBACService.deleteResource(resourceId),
      () => dispatch(initializeRbac()).unwrap(),
      "Failed to delete resource",
      "Resource deleted",
      rejectWithValue
    )
);

export const createRoleResource = createAsyncThunk(
  "rbac/createRoleResource",
  async (payload: { role: string; description?: string }, { dispatch, rejectWithValue }) =>
    runMutation(
      () => RBACService.createRoleResource(payload),
      () => dispatch(initializeRbac()).unwrap(),
      "Failed to create role-resource",
      `Role-resource "${payload.role}" created`,
      rejectWithValue
    )
);

export const updateRoleResource = createAsyncThunk(
  "rbac/updateRoleResource",
  async (
    payload: { roleResourceId: number; data: Record<string, unknown> },
    { dispatch, rejectWithValue }
  ) =>
    runMutation(
      () => RBACService.updateRoleResource(payload.roleResourceId, payload.data),
      () => dispatch(initializeRbac()).unwrap(),
      "Failed to update role-resource",
      "Role-resource updated",
      rejectWithValue
    )
);

export const deleteRoleResource = createAsyncThunk(
  "rbac/deleteRoleResource",
  async (roleResourceId: number, { dispatch, rejectWithValue }) =>
    runMutation(
      () => RBACService.deleteRoleResource(roleResourceId),
      () => dispatch(initializeRbac()).unwrap(),
      "Failed to delete role-resource",
      "Role-resource deleted",
      rejectWithValue
    )
);

export const createRoleResourcePermission = createAsyncThunk(
  "rbac/createRoleResourcePermission",
  async (
    payload: { roleResourceId: number; resourceIds: number[]; permissionIds: number[] },
    { dispatch, rejectWithValue }
  ) =>
    runMutation(
      () => RBACService.createRoleResourcePermissions(payload),
      () => dispatch(initializeRbac()).unwrap(),
      "Failed to create mapping",
      `Created ${payload.resourceIds.length * payload.permissionIds.length} mapping(s)`,
      rejectWithValue
    )
);

export const updateRoleResourcePermission = createAsyncThunk(
  "rbac/updateRoleResourcePermission",
  async (
    payload: {
      roleResourcePermissionId: number;
      granteeRoleResourceId: number;
      resourceId: number;
      permissionId: number;
    },
    { dispatch, rejectWithValue }
  ) =>
    runMutation(
      () =>
        RBACService.updateRoleResourcePermission(payload.roleResourcePermissionId, {
          roleId: payload.granteeRoleResourceId,
          resourceId: payload.resourceId,
          permissionId: payload.permissionId,
        }),
      () => dispatch(initializeRbac()).unwrap(),
      "Failed to update mapping",
      "Mapping updated",
      rejectWithValue
    )
);

export const deleteRoleResourcePermission = createAsyncThunk(
  "rbac/deleteRoleResourcePermission",
  async (roleResourcePermissionId: number, { dispatch, rejectWithValue }) =>
    runMutation(
      () => RBACService.deleteRoleResourcePermission(roleResourcePermissionId),
      () => dispatch(initializeRbac()).unwrap(),
      "Failed to delete mapping",
      "Mapping deleted",
      rejectWithValue
    )
);

export const createAuthorizationUser = createAsyncThunk(
  "rbac/createAuthorizationUser",
  async (
    payload: { roleResourceId: number; name: string; email: string; password: string },
    { dispatch, rejectWithValue }
  ) =>
    runMutation(
      () => RBACService.createUser(payload),
      () => dispatch(initializeRbac()).unwrap(),
      "Failed to create user",
      `User "${payload.name}" created`,
      rejectWithValue
    )
);

export const updateAuthorizationUser = createAsyncThunk(
  "rbac/updateAuthorizationUser",
  async (
    payload: { userId: number; roleResourceId: number },
    { dispatch, rejectWithValue }
  ) =>
    runMutation(
      () => RBACService.updateUserRoleAssignment(payload),
      () => dispatch(initializeRbac()).unwrap(),
      "Failed to update user assignment",
      "User reassigned",
      rejectWithValue
    )
);

export const fetchRoleResourcePermissionViews = createAsyncThunk(
  "rbac/fetchRoleResourcePermissionViews",
  async (roleResourceId: number, { rejectWithValue }) => {
    try {
      const [grouped, adminPortal] = await Promise.all([
        RBACService.getGroupedPermissionsForRoleResource(roleResourceId),
        RBACService.getAdminPortalPermissionsForRoleResource(roleResourceId),
      ]);
      return {
        roleResourceId,
        grouped: grouped as GroupedPermissionsView,
        adminPortal: adminPortal as AdminPortalPermissionMatrix,
      };
    } catch (error) {
      return rejectWithValue(mapErr(error, "Failed to fetch role-resource permissions"));
    }
  }
);

export const fetchAllotablePermissions = createAsyncThunk(
  "rbac/fetchAllotablePermissions",
  async (resourceId: number, { rejectWithValue }) => {
    try {
      const data = await RBACService.getAllotablePermissions(resourceId);
      return data as AllotablePermissionsView;
    } catch (error) {
      return rejectWithValue(mapErr(error, "Failed to fetch allotable permissions"));
    }
  }
);

const mutationThunks = [
  { thunk: createVmsRole, op: "createVmsRole" as const, idKey: undefined },
  { thunk: updateVmsRole, op: "updateVmsRole" as const, idKey: "roleId" as const },
  { thunk: deleteVmsRole, op: "deleteVmsRole" as const, isPlain: true as const },
  { thunk: createPermission, op: "createPermission" as const, idKey: undefined },
  { thunk: updatePermission, op: "updatePermission" as const, idKey: "permissionId" as const },
  { thunk: deletePermission, op: "deletePermission" as const, isPlain: true as const },
  { thunk: createResource, op: "createResource" as const, idKey: undefined },
  { thunk: updateResource, op: "updateResource" as const, idKey: "resourceId" as const },
  { thunk: deleteResource, op: "deleteResource" as const, isPlain: true as const },
  { thunk: createRoleResource, op: "createRoleResource" as const, idKey: undefined },
  { thunk: updateRoleResource, op: "updateRoleResource" as const, idKey: "roleResourceId" as const },
  { thunk: deleteRoleResource, op: "deleteRoleResource" as const, isPlain: true as const },
  { thunk: createRoleResourcePermission, op: "createRoleResourcePermission" as const, idKey: "roleResourceId" as const },
  {
    thunk: updateRoleResourcePermission,
    op: "updateRoleResourcePermission" as const,
    idKey: "roleResourcePermissionId" as const,
  },
  { thunk: deleteRoleResourcePermission, op: "deleteRoleResourcePermission" as const, isPlain: true as const },
  { thunk: createAuthorizationUser, op: "createAuthorizationUser" as const, idKey: undefined },
  { thunk: updateAuthorizationUser, op: "updateAuthorizationUser" as const, idKey: "userId" as const },
];

const pickActionId = (
  meta: { arg: unknown },
  op: string,
  idKey: string | undefined,
  isPlain: boolean
): string => {
  if (isPlain && (typeof meta.arg === "number" || typeof meta.arg === "string")) {
    return buildActionId(op, meta.arg as number | string);
  }
  if (idKey && meta.arg && typeof meta.arg === "object") {
    const val = (meta.arg as Record<string, unknown>)[idKey];
    if (val != null && (typeof val === "number" || typeof val === "string")) {
      return buildActionId(op, val);
    }
  }
  return buildActionId(op);
};

const rbacSlice = createSlice({
  name: "rbac",
  initialState,
  reducers: {
    pushToast: (state, action: PayloadAction<Omit<RbacToastMessage, "id">>) => {
      state.toasts.push({ id: Date.now() + Math.random(), ...action.payload });
    },
    dismissToast: (state, action: PayloadAction<number>) => {
      state.toasts = state.toasts.filter((t) => t.id !== action.payload);
    },
    clearToasts: (state) => {
      state.toasts = [];
    },
    clearError: (state) => {
      state.error = null;
    },
    clearPermissionViews: (state) => {
      state.permissionViews = { roleResourceId: null, grouped: null, adminPortal: null };
      state.allotablePermissions = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(initializeRbac.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(initializeRbac.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.vmsRoles = action.payload.vmsRoles;
        state.permissions = action.payload.permissions;
        state.resources = action.payload.resources;
        state.roleResources = action.payload.roleResources;
        state.roleResourcePermissions = action.payload.roleResourcePermissions;
        state.users = action.payload.users;
      })
      .addCase(initializeRbac.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) ?? "Failed to load RBAC data";
      })
      .addCase(fetchRoleResourcePermissionViews.pending, (state) => {
        state.viewsLoading = true;
      })
      .addCase(fetchRoleResourcePermissionViews.fulfilled, (state, action) => {
        state.viewsLoading = false;
        state.permissionViews = {
          roleResourceId: action.payload.roleResourceId,
          grouped: action.payload.grouped,
          adminPortal: action.payload.adminPortal,
        };
      })
      .addCase(fetchRoleResourcePermissionViews.rejected, (state, action) => {
        state.viewsLoading = false;
        state.toasts.push({
          id: Date.now() + Math.random(),
          kind: "error",
          message: (action.payload as string) ?? "Failed to fetch permission views",
        });
      })
      .addCase(fetchAllotablePermissions.fulfilled, (state, action) => {
        state.allotablePermissions = action.payload;
      })
      .addCase(fetchAllotablePermissions.rejected, (state, action) => {
        state.toasts.push({
          id: Date.now() + Math.random(),
          kind: "error",
          message: (action.payload as string) ?? "Failed to fetch allotable permissions",
        });
      });

    mutationThunks.forEach((entry) => {
      const { thunk, op } = entry;
      const idKey = (entry as { idKey?: string }).idKey;
      const isPlain = Boolean((entry as { isPlain?: boolean }).isPlain);
      builder
        .addCase(thunk.pending, (state, action) => {
          state.pendingActionId = pickActionId(action.meta, op, idKey, isPlain);
        })
        .addCase(thunk.fulfilled, (state, action) => {
          state.pendingActionId = null;
          const message = (action.payload as { successMessage?: string } | undefined)?.successMessage;
          if (message) {
            state.toasts.push({ id: Date.now() + Math.random(), kind: "success", message });
          }
        })
        .addCase(thunk.rejected, (state, action) => {
          state.pendingActionId = null;
          const message =
            (action.payload as { message?: string } | undefined)?.message ??
            (action.payload as string | undefined) ??
            `Action "${op}" failed`;
          state.toasts.push({ id: Date.now() + Math.random(), kind: "error", message });
        });
    });
  },
});

export const { pushToast, dismissToast, clearToasts, clearError, clearPermissionViews } =
  rbacSlice.actions;

export default rbacSlice.reducer;
